import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Target, Layers, Zap, BarChart3, Users, Lightbulb } from "lucide-react";

const HowToGamifyYourApp = () => {
  const navigate = useNavigate();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How to Gamify Your App: A Strategy Guide for Developers (2026)",
    "description": "Most gamification guides give you theory. This one gives you a personalised strategy for your specific app — in 10 minutes.",
    "datePublished": "2026-04-13",
    "dateModified": "2026-04-13",
    "author": {
      "@type": "Organization",
      "name": "Gamification Strategy Generator",
    },
    "publisher": {
      "@type": "Organization",
      "name": "Gamification Strategy Generator",
      "url": "https://gamifyme-scorecard.lovable.app",
    },
    "mainEntityOfPage": "https://gamifyme-scorecard.lovable.app/how-to-gamify-your-app",
  };

  return (
    <>
      <SEO
        title="How to Gamify Your App: A Strategy Guide for Developers (2026)"
        description="Most gamification guides give you theory. This one gives you a personalised strategy for your specific app — in 10 minutes."
        canonicalUrl="https://gamifyme-scorecard.lovable.app/how-to-gamify-your-app"
        keywords="how to gamify app, gamification strategy, app gamification guide, game mechanics for apps, developer gamification"
        jsonLd={articleSchema}
      />
      <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background">
        <Navigation />
        <Breadcrumbs />

        <main className="pt-32 pb-20 px-4">
          <article className="max-w-4xl mx-auto space-y-16">
            {/* Hero */}
            <header className="text-center space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                How to Gamify Your App: A Strategy Guide for Developers
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Most gamification guides give you theory. This one gives you a personalised strategy for your specific app — in 10 minutes.
              </p>
              <Button
                size="lg"
                onClick={() => navigate("/assessment")}
                className="text-lg px-10 py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity shadow-lg"
              >
                Get your personalised strategy — $5
              </Button>
            </header>

            {/* Section 1 */}
            <section className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">Why Most Gamification Attempts Fail</h2>
              <p className="text-lg text-muted-foreground">
                Adding points and badges to your app isn't gamification — it's decoration. True gamification aligns game mechanics with your users' core motivations and your product's goals. Without that alignment, you end up with features nobody uses.
              </p>
              <p className="text-lg text-muted-foreground">
                The difference between apps that retain users and apps that don't comes down to one thing: whether the gamification strategy was designed for their specific context, not copied from a generic playbook.
              </p>
            </section>

            {/* Section 2 */}
            <section className="space-y-8">
              <h2 className="text-3xl md:text-4xl font-bold">The 6-Step Gamification Framework</h2>
              <p className="text-lg text-muted-foreground">
                Every successful gamification implementation follows a structured approach. Here's the framework used by top-performing apps:
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { icon: Target, title: "1. Define Your Core Loop", desc: "Identify the single most important action you want users to repeat. Everything else is built around this." },
                  { icon: Users, title: "2. Map User Motivations", desc: "Understand whether your users are driven by achievement, social connection, exploration, or competition." },
                  { icon: Layers, title: "3. Choose the Right Mechanics", desc: "Select 2–3 game mechanics that align with your users' motivations — not the ones that look coolest." },
                  { icon: Zap, title: "4. Design Feedback Loops", desc: "Create immediate, visible responses to user actions. Delayed feedback kills engagement." },
                  { icon: BarChart3, title: "5. Build Progression Systems", desc: "Give users a clear sense of advancement — levels, unlocks, milestones that feel earned." },
                  { icon: Lightbulb, title: "6. Iterate With Data", desc: "Measure what works, remove what doesn't. Gamification is a living system, not a one-time feature." },
                ].map((step) => (
                  <Card key={step.title} className="p-6 space-y-3 border-2 border-transparent hover:border-primary/20 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                        <step.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold">{step.title}</h3>
                    </div>
                    <p className="text-muted-foreground">{step.desc}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* Section 3 */}
            <section className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">Which Mechanics Work for Which App Types</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Utility & Productivity Apps</h3>
                  <p className="text-muted-foreground">
                    Focus on streaks, progress tracking, and milestone rewards. Users want to feel productive — don't distract them with social competition. Think Todoist's karma system or Notion's usage stats.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Health & Fitness Apps</h3>
                  <p className="text-muted-foreground">
                    Challenges, social accountability, and variable rewards work best. The key is making consistency feel rewarding before results appear. Think Strava's segments or Apple Fitness rings.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Education & Learning Apps</h3>
                  <p className="text-muted-foreground">
                    Spaced repetition, XP systems, and league tables drive engagement. The trick is making learning feel like play without trivialising the content. Think Duolingo's streak system.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Social & Community Apps</h3>
                  <p className="text-muted-foreground">
                    Reputation systems, contribution badges, and tiered access levels create healthy engagement loops. The mechanics should encourage quality participation, not just volume.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">Common Mistakes to Avoid</h2>
              <ul className="space-y-3 text-lg text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold mt-0.5">×</span>
                  <span>Over-gamifying — adding too many mechanics at once overwhelms users</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold mt-0.5">×</span>
                  <span>Rewarding the wrong behaviours — gamifying vanity metrics instead of value-creating actions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold mt-0.5">×</span>
                  <span>Copying competitors — what works for Duolingo won't work for your fintech app</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold mt-0.5">×</span>
                  <span>Ignoring intrinsic motivation — extrinsic rewards alone create dependency, not loyalty</span>
                </li>
              </ul>
            </section>

            {/* Bottom CTA */}
            <section className="py-16 text-center space-y-8 border-t border-border">
              <h2 className="text-3xl md:text-4xl font-bold">
                Skip the Guesswork
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Answer 8 questions about your app and get a personalised gamification strategy — built by AI, tailored to your specific product and audience.
              </p>
              <Button
                size="lg"
                onClick={() => navigate("/assessment")}
                className="text-lg px-10 py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity shadow-lg"
              >
                Get your personalised strategy — $5
              </Button>
            </section>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default HowToGamifyYourApp;
